import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { AuthProvider } from '@/auth/auth-provider'
import { useAuth } from '@/auth/use-auth'
import { Spinner } from '@/components/ui/spinner'
import { createQueryClient } from '@/lib/query-client'
import { router } from '@/router'

export function App() {
  const [queryClient] = useState(createQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RoutedApplication />
      </AuthProvider>
    </QueryClientProvider>
  )
}

/**
 * Holds the router back until the session question is settled.
 *
 * The access token is memory-only, so every reload starts anonymous and asks the refresh cookie
 * for a new one. Mounting the router before that answer arrives would bounce a signed-in user to
 * the sign-in screen for a moment on every refresh — and losing an operator's place mid-task is
 * worse than a brief splash.
 */
function RoutedApplication() {
  const auth = useAuth()

  // Route guards read auth from the router context; re-evaluating the matches on a status change
  // is what makes a sign-out take effect on the screen the user is already standing on.
  //
  // Nothing is invalidated while the session is still being restored: the router below has not
  // mounted yet, so its context holds no auth, and a guard re-run at that moment would read it.
  useEffect(() => {
    if (auth.status === 'initialising') {
      return
    }

    void router.invalidate()
  }, [auth.status, auth.user])

  if (auth.status === 'initialising') {
    return (
      <div className="flex h-full items-center justify-center bg-canvas">
        <Spinner label="Restoring session…" />
      </div>
    )
  }

  return <RouterProvider router={router} context={{ auth }} />
}
